import React, { useCallback, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { useAppSelector } from '../../../../app/hooks';
import GstDetailsBody, { hasAnyGstData } from './GstDetailsBody';
import GstSubTabBar from './GstSubTabBar';
import GstAnalysisPanel from './GstAnalysisPanel';
import { fetchGstOverviewData } from '../../../../store/dashboard/gstOverview/apiHelper';
import { GstOverviewResponse } from '../../../../store/dashboard/gstOverview/types';
import SignatoriesPill from './SignatoriesPill';
import { useIsMobile } from '../../../../common/_custom/Hooks/useIsMobile';
import { useGstTabBarActions } from './useGstTabBarActions';
import { useGstOverviewPolling } from './useGstOverviewPolling';
import { OVERVIEW_PILL_ID, REVENUE_PILL_ID, EXPENSES_PILL_ID, useGstOverviewLogic } from './useGstOverviewLogic';
import { TabModule } from '../../../../utils/constants/commonConstants';
import LoadingSpinner from '../../../../common/_custom/LoadingSpinner/LoadingSpinner';
import styles from './GstOverview.module.scss';

interface GstOverviewProps {
    isPDFReport?: boolean;
    pdfReportSection?: string;
}

function GstOverview({ isPDFReport = false, pdfReportSection = '' }: GstOverviewProps) {
    const { orgId, gstSubTabs, noDataPills, activePill, handlePillSelect, modulePollingList } = useGstOverviewLogic();
    // Lifted out of GstOverviewBody so it stays in scope for GstSubTabBar's actions below
    // (GstOverviewBody remounts on pill switch — see its own comment — so its state can't feed
    // a sibling that renders earlier in the tree).
    const [data, setData] = useState<GstOverviewResponse | null>(null);
    const isMobile = useIsMobile();
    const isPdfView = isPDFReport || !!pdfReportSection;
    const { actions, menuTriggerRef, overlays } = useGstTabBarActions(orgId, hasAnyGstData(data), isPdfView);
    const showActions = !isPdfView && !isMobile;

    return (
        <div className={styles.GstSidebarTab}>
            <GstSubTabBar
                subTabs={gstSubTabs}
                activePill={activePill}
                noDataPills={noDataPills}
                onSelect={handlePillSelect}
                actions={showActions ? actions : undefined}
                menuTriggerRef={menuTriggerRef}
            />
            {showActions && overlays}
            {activePill === OVERVIEW_PILL_ID && (
                <GstOverviewBody orgId={orgId} data={data} setData={setData} modulePollingList={modulePollingList} />
            )}
            {activePill === REVENUE_PILL_ID && (
                <GstAnalysisPanel tab={TabModule.REVENUE} isPDFReport={isPDFReport} pdfReportSection={pdfReportSection} />
            )}
            {activePill === EXPENSES_PILL_ID && (
                <GstAnalysisPanel tab={TabModule.VENDOR_EXPENSES} isPDFReport={isPDFReport} pdfReportSection={pdfReportSection} />
            )}
        </div>
    );
}

interface GstOverviewBodyProps {
    orgId: string;
    data: GstOverviewResponse | null;
    setData: React.Dispatch<React.SetStateAction<GstOverviewResponse | null>>;
    modulePollingList: string[];
}

// Separate component so useGstOverviewPolling + in-flight effects only run
// when the user is actually on the overview pill (remount-on-pill-switch).
// `data`/`setData` are owned by the parent (see GstOverview) so the last-fetched
// value survives this component unmounting when the user switches pills.
function GstOverviewBody({ orgId, data, setData, modulePollingList }: GstOverviewBodyProps) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchData = useCallback(
        async (isPollingCall = false) => {
            if (!orgId) return;
            if (!isPollingCall) {
                setLoading(true);
                setError(null);
            }
            try {
                const result = await fetchGstOverviewData(orgId);
                setData(result);
            } catch {
                if (!isPollingCall) setError('Failed to load GST overview');
            } finally {
                if (!isPollingCall) setLoading(false);
            }
        },
        [orgId],
    );

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Two in-flight signals converge: BE task status (survives reload) + the
    // drawer-click signal (fires before BE writes the task status).
    const taskStatus = data?.AICA_GET_TASK_TRACKER_DATA?.taskTrackerData?.AION_INGEST_GST_PROFILE;
    const isStatusInFlight = !!taskStatus && taskStatus !== 'COMPLETE' && taskStatus !== 'FAILED' && taskStatus !== 'FAILURE';
    const isModulePolling = modulePollingList.includes(TabModule.GST);
    const isInFlight = isStatusInFlight || isModulePolling;

    useGstOverviewPolling(fetchData, isInFlight);

    const lastUpdatedLabel = data?.lastUpdatedAt ? dayjs(data.lastUpdatedAt).format('DD MMM YYYY') : '';

    return (
        <div className={styles.GstPage}>
            <div className={styles.SectionHeader}>
                <div className={styles.SectionTitle}>GST Overview</div>
                <div className={styles.HeaderMeta}>
                    {isInFlight && (
                        <span className={styles.RefreshingText} aria-live="polite">
                            Fetching Data...
                            <LoadingSpinner color="var(--primary-text-colour)" height="20px" />
                        </span>
                    )}
                    {lastUpdatedLabel && <span className={styles.LastUpdated}>Last Updated on {lastUpdatedLabel}</span>}
                    {data && <SignatoriesPill signatories={data.summary?.signatories || []} />}
                    <span className={styles.SourcePill}>
                        Source: <span className={styles.SourceValue}>GST</span>
                    </span>
                </div>
            </div>
            <GstDetailsBody loading={loading || (isInFlight && !data)} error={error} data={data} />
        </div>
    );
}

export default GstOverview;
